// Version mobile responsive pour l'interface Admin - Explications
import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useRequestsApi } from "../../../hooks/useRequestsApi";
import PendingRequests from "./PendingRequests";
import RequestDetailsModal from "./RequestDetailsModal";
import AssignTeacherModal from "./AssignTeacherModal";
import RejectRequestModal from "./RejectRequestModal";

// Composants mobiles
import MobileCard from "../../common/MobileComponents/MobileCard";

const MobileAdminExplanations: React.FC = () => {
  const [allRequests, setAllRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Utiliser le hook personnalisé pour l'API
  const { getAllRequests, validateRequest, rejectRequest } = useRequestsApi();

  // Charger les données admin
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const requestsResponse = await getAllRequests();

      if (
        requestsResponse.status === 200 &&
        Array.isArray(requestsResponse.body)
      ) {
        setAllRequests(requestsResponse.body);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  // Filtrer les demandes par statut
  const getFilteredRequests = () => {
    if (!allRequests) return [];

    let filteredRequests = allRequests;

    if (selectedStatus) {
      filteredRequests = filteredRequests.filter(
        (r) => r.status === selectedStatus,
      );
    }

    return filteredRequests.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  // Voir les détails d'une demande
  const handleViewDetails = (request: any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Assigner un professeur
  const handleAssignTeacher = (request: any) => {
    setSelectedRequest(request);
    setShowAssignModal(true);
  };

  // Rejeter une demande
  const handleRejectRequest = (request: any) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  // Confirmer l'assignation d'un professeur
  const handleAssignTeacherConfirm = async (teacherId: string) => {
    if (!selectedRequest) return;

    try {
      const response = await validateRequest(selectedRequest._id, {
        teacherId,
      });

      if (response.status >= 200 && response.status < 300) {
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          toast.error(
            (response.body.message as string) || "Erreur lors de l'assignation",
          );
          return;
        }

        toast.success(
          "Professeur assigné avec succès ! Il sera notifié par email.",
        );
        setShowAssignModal(false);
        setSelectedRequest(null);
        fetchAdminData();
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de l'assignation du professeur";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(
        error?.message || "Erreur lors de l'assignation du professeur",
      );
    }
  };

  // Confirmer le rejet d'une demande
  const handleRejectRequestConfirm = async (rejectionReason: string) => {
    if (!selectedRequest) return;

    try {
      const response = await rejectRequest(selectedRequest._id, {
        rejectionReason,
      });

      if (response.status >= 200 && response.status < 300) {
        toast.success(
          "Demande rejetée avec succès ! L'élève sera notifié par email.",
        );
        setShowRejectModal(false);
        setSelectedRequest(null);
        fetchAdminData();
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors du rejet de la demande";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(error?.message || "Erreur lors du rejet de la demande");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header mobile */}
      <div className="bg-white border-b border-stroke sticky top-0 z-10">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">
            Administration des explications
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gérez toutes les demandes d'explications
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Filtre de statut */}
        <div className="bg-white rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par statut
          </label>
          <select
            value={selectedStatus}
            onChange={handleStatusChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="validated">Validée</option>
            <option value="slots_proposed">Créneaux proposés</option>
            <option value="student_confirmed">Créneau confirmé</option>
            <option value="meeting_scheduled">Réunion programmée</option>
            <option value="scheduled">Programmée</option>
            <option value="completed">Terminée</option>
            <option value="rejected">Rejetée</option>
          </select>
        </div>

        {/* Liste des demandes */}
        <MobileCard>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Demandes d'explications
              </h2>
              <span className="text-sm text-gray-500">
                {getFilteredRequests().length} demande
                {getFilteredRequests().length > 1 ? "s" : ""}
              </span>
            </div>

            {getFilteredRequests().length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Aucune demande trouvée</p>
              </div>
            ) : (
              <PendingRequests
                requests={getFilteredRequests()}
                onViewDetails={handleViewDetails}
                onAssignTeacher={handleAssignTeacher}
                onRejectRequest={handleRejectRequest}
              />
            )}
          </div>
        </MobileCard>

        {/* Modales */}
        {showDetailsModal && selectedRequest && (
          <RequestDetailsModal
            open={showDetailsModal}
            request={selectedRequest}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
          />
        )}

        {showAssignModal && selectedRequest && (
          <AssignTeacherModal
            open={showAssignModal}
            request={selectedRequest}
            onAssign={handleAssignTeacherConfirm}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedRequest(null);
            }}
          />
        )}

        {showRejectModal && selectedRequest && (
          <RejectRequestModal
            open={showRejectModal}
            request={selectedRequest}
            onReject={handleRejectRequestConfirm}
            onClose={() => {
              setShowRejectModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MobileAdminExplanations;
